import { type DeviceEntity, type SiteId } from '@solinkcorp/api-types';
import { getLocalTimeZone } from '@solinkcorp/utils';
import mergeWith from 'lodash/mergeWith';
import { type CamerasDictionaryCamera, type VideoDiscoverySite } from 'utils/types';
import { DEVICE_STATUS, LOCATION_MISSED_CHECKIN_THRESHOLD_MS } from '../constants';

/**
 * returns location's timezone if only one location is selected
 */
export const getLocationTimezoneById = (
  locationsById: { [locationId: SiteId]: VideoDiscoverySite } | undefined,
  locationId: SiteId,
) => {
  const userTimezone = getLocalTimeZone();
  if (locationId === 'all-locations' || !locationsById) {
    return userTimezone;
  }

  const desiredLocation = locationsById[locationId];
  return desiredLocation?.timezone ?? userTimezone;
};

/**
 * If all locations corresponding to locationIds are in the same timezone, use the group's timezone
 * else use the user's timezone
 */
export const getLocationsTimezoneByIds = (
  locationsById: { [locationId: SiteId]: VideoDiscoverySite } | undefined,
  locationIds: readonly SiteId[],
) => {
  const userTimezone = getLocalTimeZone();
  if (locationIds[0] === 'all-locations' || !locationsById) {
    return userTimezone;
  }

  const locations = locationIds.flatMap((locationId) =>
    locationsById[locationId] ? [locationsById[locationId]] : [],
  );

  const areLocationsInSameTimzone = doLocationsHaveSameTimezone(locations);

  return areLocationsInSameTimzone ? locations[0].timezone : userTimezone;
};

function doLocationsHaveSameTimezone(locations: VideoDiscoverySite[]): boolean {
  if (!locations[0].timezone) return false;

  const currentTimezone = locations[0].timezone;

  let areLocationsInSameTimezone = true;

  for (let i = 1; i < locations.length; i++) {
    if (currentTimezone !== locations[i].timezone || !locations[i].timezone) {
      areLocationsInSameTimezone = false;
      break;
    }
  }

  return areLocationsInSameTimezone;
}
export type DeviceStatus = typeof DEVICE_STATUS.OFFLINE | typeof DEVICE_STATUS.ONLINE;

/** returns a location's status depending on check in time and last time VD updated the location */
export function getDeviceStatus({
  nextCheckin,
  lastUpdateTime,
}: {
  nextCheckin?: Date;
  lastUpdateTime: Date;
}): DeviceStatus {
  if (!nextCheckin) {
    return DEVICE_STATUS.OFFLINE;
  }

  return +lastUpdateTime - +new Date(nextCheckin) > LOCATION_MISSED_CHECKIN_THRESHOLD_MS
    ? DEVICE_STATUS.OFFLINE
    : DEVICE_STATUS.ONLINE;
}

export type DeviceStatuses = Record<string, DeviceStatus>;

export function getDeviceStatuses({
  devices,
  lastUpdateTime,
}: {
  devices: DeviceEntity[];
  lastUpdateTime: Date;
}): DeviceStatuses {
  return devices.reduce(
    (acc, device) => ({
      ...acc,
      [device.id]: getDeviceStatus({ nextCheckin: device.nextCheckin, lastUpdateTime }),
    }),
    {},
  );
}

export function getOfflineDeviceCount(deviceStatuses: DeviceStatuses) {
  return Object.values(deviceStatuses).reduce((acc, status) => {
    if (status === DEVICE_STATUS.OFFLINE) {
      return acc + 1;
    }
    return acc;
  }, 0);
}

/**
 * Return a location with only the cameras specified.
 * @param locationId location ID
 * @param cameraIds filtered camera IDs under the location
 * @param locationsById all locations by location ID
 */
export function getLocationFromIds(
  locationId: string,
  cameraIds: string[],
  locationsById: { [id: string]: VideoDiscoverySite },
  camerasById: { [id: string]: CamerasDictionaryCamera },
) {
  const location = locationsById[locationId];
  const filteredLocation = { ...location };
  filteredLocation.cameras = cameraIds
    .map((cameraId: string) => camerasById[cameraId])
    .filter(Boolean);
  return filteredLocation;
}

/**
 * Return the latest filtered location with only the cameras that are filtered.
 * This is used when location is not guaranteed to be the latest.
 * @param location filtered location with filtered cameras under the location
 * @param locationsById all locations by location ID
 */
export function getLatestFilteredLocation(
  filteredLocation: VideoDiscoverySite,
  locationsById: { [id: string]: VideoDiscoverySite },
  camerasById: { [id: string]: CamerasDictionaryCamera },
) {
  const filteredCameraIds = filteredLocation.cameras?.map((camera) => camera.id) ?? [];
  return getLocationFromIds(filteredLocation.id, filteredCameraIds, locationsById, camerasById);
}

export function isPeopleTrackingEnabledForLocation(device: DeviceEntity) {
  return device?.connectSettings?.ai?.line_crossing?.enabled ?? false;
}

export function getSiteFeatures(
  devices: readonly Pick<DeviceEntity, 'id' | 'features'>[] = [],
): DeviceEntity['features'] {
  return devices.reduce(
    (acc, device) =>
      mergeWith(acc, device.features, (objValue, srcValue) => {
        if (typeof objValue === 'boolean' && typeof srcValue === 'boolean') {
          return objValue || srcValue;
        }
        return undefined;
      }),
    {},
  );
}

export function getAllSitesFeatures(sites: readonly VideoDiscoverySite[]) {
  const allDevices = sites.flatMap((site) => site.devices || []);
  return getSiteFeatures(allDevices);
}
